/**
 * Making a firm (platform staff): its name and slug, its first admin, the feed
 * it trades on and its trading terms, in one step (`POST /v1/firms`). The API
 * is the one judge of a firm; what it refuses, the form shows in its own words.
 */

import { For, JSX, Show, createResource, createSignal } from "solid-js";
import { A, useNavigate } from "@solidjs/router";

import AsyncResource from "../components/AsyncResource";
import { refetchFirms } from "../components/FirmPicker";
import PageHeader from "../components/PageHeader";
import { api, failureSentence } from "../lib/api";
import { chooseFirm } from "../lib/firm";
import { EMPTY_FIRM_FORM, type FirmForm, firmRequest } from "../lib/firmForm";

type TextField = Exclude<keyof FirmForm, "reservePendingOrderMargin">;

export default function FirmCreate() {
  const navigate = useNavigate();
  const [feeds] = createResource(() => api.listFeeds());
  const [form, setForm] = createSignal<FirmForm>(EMPTY_FIRM_FORM);
  const [error, setError] = createSignal<string | null>(null);
  const [saving, setSaving] = createSignal(false);
  const [existed, setExisted] = createSignal(false);

  function set<K extends keyof FirmForm>(field: K, value: FirmForm[K]) {
    setForm((current) => ({ ...current, [field]: value }));
    setError(null);
    setExisted(false);
  }

  function text(
    field: TextField,
    label: string,
    attrs: JSX.InputHTMLAttributes<HTMLInputElement> = {},
  ): JSX.Element {
    const id = `firm-${field}`;
    return (
      <div>
        <label class="label" for={id}>
          {label}
        </label>
        <input
          id={id}
          class="input"
          {...attrs}
          value={form()[field]}
          onInput={(e) => set(field, e.currentTarget.value)}
        />
      </div>
    );
  }

  async function onCreate(ev: Event) {
    ev.preventDefault();
    const built = firmRequest(form());
    if (!built.ok) {
      setError(built.error);
      return;
    }
    setSaving(true);
    setError(null);
    setExisted(false);
    try {
      const firm = await api.createFirm(built.request);
      const listed = (await refetchFirms())?.organizations.find(
        (org) => org.org_id === firm.org_id,
      );
      if (!listed) {
        setError("The firm was made, but the firm list does not show it yet; choose it in the firm picker.");
        return;
      }
      chooseFirm({ orgId: listed.org_id, governance: listed.governance });
      if (firm.created) {
        navigate("/operators");
      } else {
        setExisted(true);
      }
    } catch (e) {
      setError(failureSentence(e));
    } finally {
      setSaving(false);
    }
  }

  const decimal = { inputmode: "decimal" } as const;

  return (
    <>
      <PageHeader
        title="New firm"
        subtitle="A firm, whole: its first admin, feed, standard profiles and trading terms"
      />
      <AsyncResource resource={feeds}>
        {(available) => (
          <div class="card">
            <div class="card-body">
              <form class="space-y-4" onSubmit={onCreate}>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {text("name", "Name")}
                  {text("slug", "Slug", { class: "input font-mono text-xs" })}
                  <div>
                    {text("firstAdmin", "First admin (their Noctant login's address)", {
                      type: "email",
                    })}
                    <p class="mt-1 text-xs text-slate-500">
                      They sign in to this console with that login once its address is proven.
                    </p>
                  </div>
                  <div>
                    <label class="label" for="firm-feedKey">
                      Feed
                    </label>
                    <select
                      id="firm-feedKey"
                      class="select"
                      value={form().feedKey}
                      onChange={(e) => set("feedKey", e.currentTarget.value)}
                    >
                      <option value="">Choose a feed…</option>
                      <For each={available().feeds}>
                        {(feed) => (
                          <option value={String(feed.feed_key)}>
                            {feed.feed_code} ({feed.feed_key})
                          </option>
                        )}
                      </For>
                    </select>
                  </div>
                </div>
                <h2 class="text-sm font-semibold">Trading terms</h2>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {text("marginRequirement", "Margin requirement", decimal)}
                  {text("maxPositionSize", "Max position size", decimal)}
                  {text("maxOpenOrders", "Max open orders", { inputmode: "numeric" })}
                  {text("marginCallLevel", "Margin call level", decimal)}
                  {text("stopOutLevel", "Stop-out level", decimal)}
                  <label class="inline-flex items-center gap-2 self-end">
                    <input
                      type="checkbox"
                      checked={form().reservePendingOrderMargin}
                      onChange={(e) =>
                        set("reservePendingOrderMargin", e.currentTarget.checked)
                      }
                    />
                    <span class="text-sm">Reserve margin for pending orders</span>
                  </label>
                  {text("allowedSymbols", "Allowed symbols (commas or spaces; none allows all)", {
                    class: "input font-mono text-xs",
                  })}
                  {text("blockedSymbols", "Blocked symbols", { class: "input font-mono text-xs" })}
                </div>
                <Show when={error()}>
                  <p class="text-sm text-red-600">{error()}</p>
                </Show>
                <Show when={existed()}>
                  <p class="text-sm text-slate-700">
                    This firm already exists exactly as you asked; it is now the chosen firm.{" "}
                    <A href="/operators" class="underline">
                      Go to its operators
                    </A>
                  </p>
                </Show>
                <button type="submit" class="btn-primary" disabled={saving()}>
                  {saving() ? "Making the firm…" : "Make the firm"}
                </button>
              </form>
            </div>
          </div>
        )}
      </AsyncResource>
    </>
  );
}
